import './bar-chart.css';

import { Text } from '@kerfjs/ui/text';
import { type SafeHtml } from 'kerfjs';

export interface BarChartBar {
  /** Stable key for keyed rendering (for example `2026-09` or `2026-09-20`). */
  key: string;
  /**
   * Bar height is |value| relative to the chart's peak |value|, so the peak bar is always full
   * height, however small the values are; zero draws the one-pixel baseline (HS2-9H21V8).
   */
  value: number;
  /** Axis label under the bar. An empty string keeps the slot but shows no label (sparse axes). */
  label: string;
  /** Hover tooltip, normally the full date and formatted amount. */
  title: string;
}

/** Bar count above which bars sit closer together so a month of days fits a phone width. */
const DENSE_BAR_COUNT = 16;

/** Non-breaking space: holds an empty label's line and keeps "Oct 12" on one line over a narrow column. */
const NBSP = String.fromCharCode(0xa0);

/**
 * A plain vertical bar chart shared by the Stats "By month" savings chart and the AI
 * usage last-30-days chart (HS2-GH14FR). No divider or caption; zero values render
 * as a one-pixel baseline and every bar carries a `title` tooltip.
 */
export function BarChart({ bars, label }: { bars: readonly BarChartBar[]; label: string }): SafeHtml {
  // Normalize to the peak, not to a floor of 1: sub-unit series (cents of AI spend) still fill the chart.
  // An all-zero chart has no peak and draws only baselines, which never read `peak` (HS2-9H21V8).
  const peak = Math.max(0, ...bars.map((bar) => Math.abs(bar.value)));
  return (
    <div class="bar-chart" role="img" aria-label={label} data-dense={String(bars.length > DENSE_BAR_COUNT)}>
      {bars.map((bar) => (
        <div class="bar-chart-col" data-key={bar.key} title={bar.title}>
          {/* The plot box excludes the label, so a bar's percentage height is a true share of the peak. */}
          <span class="bar-chart-plot">
            <span
              class="bar-chart-bar"
              data-zero={String(bar.value === 0)}
              style={bar.value === 0 ? undefined : `height: ${String(Math.round((Math.abs(bar.value) / peak) * 100))}%`}
            />
          </span>
          <span class="bar-chart-label" aria-hidden="true">
            <Text variant="span" tone="quiet" size="compact">
              {bar.label === '' ? NBSP : bar.label.replace(/ /g, NBSP)}
            </Text>
          </span>
        </div>
      ))}
    </div>
  );
}
